import { useState } from "react";
import { useFragment } from "react-relay";
import { graphql } from "relay-runtime";
import { Card, i18n } from "rujira.ui";
import { AngleDown } from "rujira.ui/src/components/icons/Icons";
import { FAQFragment$key } from "./__generated__/FAQFragment.graphql";

const fragment = graphql`
  fragment FAQFragment on IndexVault {
    address
    fees {
      rates {
        management
        transaction
      }
    }
    shareAsset {
      metadata {
        symbol
      }
    }
    type
  }
`;

export const FAQ = ({ k }: { k: FAQFragment$key }) => {
  const [open, setOpen] = useState<number | null>(null);
  const { fees, shareAsset, type, address } = useFragment(fragment, k);
  const methodologyUrl = shareAsset?.metadata.symbol
    ? `https://docs.rujira.network/core-products/ruji-index/${shareAsset?.metadata.symbol.toLowerCase()}`
    : "https://docs.rujira.network/core-products/ruji-index";
  const contractUrl = `https://thorchain.net/address/${address}`;

  const FAQ_ITEMS = [
    {
      title: "What is the asset methodology?",
      content: `The methodology is different for each index. <br/><br/><a href="${methodologyUrl}" target="_blank" rel="noopener noreferrer">Read about the details in the Rujira docs.</a>`,
    },
    {
      title: "How does rebalancing work?",
      content:
        type == "nav"
          ? `The contract automatically keeps the defined balance between assets. If the ratio between them changes more than the set threshold, it will adjust the holdings to bring them back to the target mix. <br/><br/><a href="https://docs.rujira.network/core-products/ruji-index" target="_blank" rel="noopener noreferrer">Read more in the Rujira docs.</a>`
          : `Fixed unit indexes don’t rebalance. They keep the same asset ratio set at creation, unless tokens are added or removed. <br/><br/><a href="https://docs.rujira.network/core-products/ruji-index" target="_blank" rel="noopener noreferrer">Read more in the Rujira docs.</a>`,
    },
    {
      title: "Where does the yield come from?",
      content: `${shareAsset.metadata.symbol} accrues value in two ways:\n\n1. Staking yield: the index earns a share of TCY staking rewards (currently 10% of THORChain’s revenue).\n\n2. Trading profit: the index captures price swings between the underlying assets (see Asset Methodology).\n\n3. Price Appreciation: The index value increases when the underlying assets appreciate in price.<br/>The yield is automatically reinvested into the index, increasing its value. <br/><br/><a href="${methodologyUrl}" target="_blank" rel="noopener noreferrer">Read more in the Rujira docs.</a>`,
      indexes: ["yRUNE", "yTCY"],
    },
    {
      title: "What are the risks?",
      content: `The following risks may apply to this digital asset:<br/><br/>
- Loss of digital assets (full or partial) due to technical hacks, exploits, or failures at the protocol or smart contract level.<br/>
- Restrictions imposed by regulatory authorities in the end user’s jurisdiction that may limit access to or use of the digital assets.<br/>
- Loss of digital assets or loss of access to those assets resulting from decisions made by centralized providers of the underlying tokens.<br/>
- Loss of digital assets (full or partial) through normal product operations when unexpected market conditions arise.<br/>
- Loss of digital assets (full or partial) due to changes in underlying product assets implemented by the originating protocols.<br/>
- Loss of digital assets (full or partial) stemming from volatility, correlation, value-at-risk, or contagion risks.<br/>
- Underperformance of digital assets when the actual methodology deviates from its intended design.<br/>
- Loss of digital assets (full or partial) due to the inherent volatility of the underlying tokens.`,
    },
    {
      title: "What fees apply?",
      content: `<b>Minting Fee:</b> None<br/><br/><b>Withdrawal Fee:</b> ${(Number(fees.rates.transaction) / 10_000_000_000).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%<br/><br/><b>Management Fee:</b> ${(Number(fees.rates.management) / 10_000_000_000).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}% p.a.<br/><br/><a href="https://docs.rujira.network/core-products/ruji-index" target="_blank" rel="noopener noreferrer">Read more in the Rujira docs.</a>`,
    },
    {
      title: "What's the contract address?",
      content: `The contract address is <a href="${contractUrl}" target="_blank" rel="noopener noreferrer">${address}</a>.`,
    },
  ];

  const filteredFAQ = FAQ_ITEMS.filter((item) => {
    if (!item.indexes) return true;
    return item.indexes
      .map((i) => i.toLowerCase())
      .includes(shareAsset.metadata.symbol.toLowerCase());
  });

  return (
    <Card className="p-10 mt-2 index__mobile-adjust">
      <i18n.h2 className="fs-24 lh-32 fw-400 color-white">FAQ</i18n.h2>
      <div className="index__faq-accordion">
        {filteredFAQ.map((item, idx) => (
          <div className="index__faq-item" key={item.title}>
            <button
              className="index__faq-question"
              onClick={() => setOpen(open === idx ? null : idx)}
              aria-expanded={open === idx}
              aria-controls={`index__faq-panel-${idx}`}>
              <span>{item.title}</span>
              <span
                className={
                  open === idx
                    ? "index__faq-icon index__faq-icon--open"
                    : "index__faq-icon"
                }>
                <AngleDown color="#ffffff" width={20} />
              </span>
            </button>
            <div
              className="index__faq-answer"
              id={`index__faq-panel-${idx}`}
              style={{
                display: open === idx ? "block" : "none",
                whiteSpace: "pre-wrap",
              }}
              dangerouslySetInnerHTML={{ __html: item.content }}
            />
          </div>
        ))}
      </div>
    </Card>
  );
};
